import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./ProjectMasterTable.css";

// "09:00:00" → "9:00"
const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = String(t).split(":");
    return `${Number(h)}:${m ?? "00"}`;
};

// 休憩時間（時間数）→ "0.75"
const fmtBreak = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return Number.isNaN(n) ? String(v) : n.toFixed(2);
};

const SPLIT_AT = 10; // 10行目まで左、11行目から右

const ProjectPatternViewer = () => {
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [shiftsList, setShiftsList] = useState([]);
    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [types, setTypes] = useState([]); // [{ key, shift_id, editing }]
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    const fetchProjects = async () => {
        try {
            const res = await api.get("/api/admin/projects/");
            const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
            setProjects(list);
        } catch (err) {
            console.error("プロジェクトの取得に失敗:", err);
        }
    };

    useEffect(() => {
        (async () => {
            await fetchProjects();
            try {
                const shiftRes = await api.get("/api/admin/shift-presets/");
                setShiftsList(Array.isArray(shiftRes.data) ? shiftRes.data : shiftRes.data?.results || []);
            } catch (err) {
                console.error("シフトの取得に失敗:", err);
            }
        })();
    }, []);

    // 勤務地の選択肢（プロジェクトから重複なしで抽出）
    const locations = useMemo(() => {
        const map = new Map();
        projects.forEach((p) => {
            if (p.work_location_id != null && !map.has(String(p.work_location_id))) {
                map.set(String(p.work_location_id), p.work_location_name || "");
            }
        });
        return Array.from(map, ([id, name]) => ({ id, name }));
    }, [projects]);

    const projectsForLocation = useMemo(
        () => projects.filter((p) => String(p.work_location_id) === String(selectedLocationId)),
        [projects, selectedLocationId]
    );

    const selectedProject = useMemo(
        () => projects.find((p) => String(p.id) === String(selectedProjectId)),
        [projects, selectedProjectId]
    );

    // プロジェクトを選択したら、そのパターンのタイプを読み込む
    useEffect(() => {
        if (!selectedProject) {
            setTypes([]);
            return;
        }
        const loaded = (selectedProject.patterns || []).flatMap((pt) => pt.types || []);
        setTypes(loaded.map((t, i) => ({ key: `t-${i}`, shift_id: String(t.shift_id), editing: false })));
        setMsg("");
    }, [selectedProject]);

    // シフトの表示ヘルパー（shiftsList から取得）
    const getShift = (shiftId) => shiftsList.find((s) => String(s.id) === String(shiftId));
    const getShiftLabel = (shiftId) => {
        const s = getShift(shiftId);
        if (!s) return "";
        return s.code ? `${s.code}${s.name && s.name !== s.code ? ` (${s.name})` : ""}` : s.name;
    };
    const getShiftTime = (shiftId, kind) => {
        const s = getShift(shiftId);
        if (!s) return "";
        if (kind === "start") return fmtTime(s.start_time);
        if (kind === "end") return fmtTime(s.end_time);
        if (kind === "break") {
            const raw = s.scheduled_break ?? s.break_hours ?? "";
            return fmtBreak(raw);
        }
        return "";
    };

    const handleLocationChange = (v) => {
        setSelectedLocationId(v);
        setSelectedProjectId("");
        setTypes([]);
    };

    const handleShiftSelect = (key, value) => {
        setTypes((prev) => prev.map((t) => (t.key === key ? { ...t, shift_id: value } : t)));
    };

    const handleEditType = (key) => {
        setTypes((prev) => prev.map((t) => (t.key === key ? { ...t, editing: true } : t)));
    };

    const handleDeleteType = (key) => {
        if (!window.confirm("このタイプを削除してもよろしいですか？")) return;
        setTypes((prev) => prev.filter((t) => t.key !== key));
    };

    const handleAddType = () => {
        setTypes((prev) => [...prev, { key: `new-${Date.now()}`, shift_id: "", editing: true }]);
    };

    const handleSave = async () => {
        if (!selectedProject) return;
        setSaving(true); setMsg("");
        try {
            const isRemote = selectedProject.remote_type === "WEEKLY" || selectedProject.remote_type === "MONTHLY";
            const patternNumber = selectedProject.patterns?.[0]?.pattern_number || 1;
            const typePayload = types
                .filter((t) => t.shift_id)
                .map((t, i) => ({ type_number: i + 1, shift_id: t.shift_id }));
            const payload = {
                project_name: selectedProject.project_name,
                work_location_id: selectedProject.work_location_id,
                is_remote_enabled: isRemote,
                remote_type: selectedProject.remote_type || "NONE",
                remote_frequency_value: isRemote && selectedProject.remote_frequency_value !== "" && selectedProject.remote_frequency_value != null
                    ? Number(selectedProject.remote_frequency_value) : null,
                patterns: [{ pattern_number: patternNumber, types: typePayload }],
            };
            await api.put(`/api/admin/projects/${selectedProject.id}/`, payload);
            await fetchProjects();
            setMsg("保存しました。");
        } catch (err) {
            console.error("保存に失敗:", err);
            const detail = err.response?.data ? JSON.stringify(err.response.data) : "";
            setMsg(`保存に失敗しました。${detail}`);
        } finally {
            setSaving(false);
        }
    };

    // タイプ編集テーブル（縦表示）。startIndex は key の一意性のみに使用。
    const renderTable = (list) => (
        <table className="project-custom-table">
            <thead>
                <tr>
                    <th>タイプ1名</th>
                    <th>出勤1</th>
                    <th>退勤1</th>
                    <th>休憩時間1</th>
                    <th style={{ minWidth: "100px" }}>操作</th>
                </tr>
            </thead>
            <tbody>
                {list.length === 0 ? (
                    <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#888", padding: "12px" }}>
                            タイプがありません。「タイプ追加」で追加してください。
                        </td>
                    </tr>
                ) : (
                    list.map((t) => (
                        <tr key={t.key}>
                            <td>
                                {t.editing ? (
                                    <select
                                        value={t.shift_id}
                                        onChange={(e) => handleShiftSelect(t.key, e.target.value)}
                                        className="table-select"
                                    >
                                        <option value="">-- 選択 --</option>
                                        {shiftsList.map((s) => (
                                            <option key={s.id} value={s.id}>{getShiftLabel(s.id)}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span style={{ fontWeight: "bold" }}>{getShiftLabel(t.shift_id)}</span>
                                )}
                            </td>
                            <td><input type="text" readOnly className="time-auto-input" value={getShiftTime(t.shift_id, "start")} placeholder="自動" /></td>
                            <td><input type="text" readOnly className="time-auto-input" value={getShiftTime(t.shift_id, "end")} placeholder="自動" /></td>
                            <td><input type="text" readOnly className="time-auto-input" value={getShiftTime(t.shift_id, "break")} placeholder="自動" /></td>
                            <td className="action-buttons-cell">
                                <div className="action-flex-box">
                                    <button type="button" className="btn-table-edit" onClick={() => handleEditType(t.key)}>修正</button>
                                    <button type="button" className="btn-table-delete" onClick={() => handleDeleteType(t.key)}>削除</button>
                                </div>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );

    const leftTypes = types.slice(0, SPLIT_AT);
    const rightTypes = types.slice(SPLIT_AT);

    return (
        <div className="project-table-container">
            <div className="project-page-header">
                <h2 className="project-page-title">📁 プロジェクト勤務地パターン</h2>
            </div>

            <div style={{ marginBottom: "12px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontWeight: "bold" }}>勤務地</label>
                <select value={selectedLocationId} onChange={(e) => handleLocationChange(e.target.value)} className="table-select" style={{ minWidth: "240px" }}>
                    <option value="">-- 選択 --</option>
                    {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                </select>

                <label style={{ fontWeight: "bold" }}>プロジェクト</label>
                <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className="table-select" style={{ minWidth: "240px" }} disabled={!selectedLocationId}>
                    <option value="">-- 選択 --</option>
                    {projectsForLocation.map((p) => (
                        <option key={p.id} value={String(p.id)}>{p.project_name}</option>
                    ))}
                </select>

                {selectedProject && (
                    <span style={{ fontWeight: "bold", color: "#2563eb", fontSize: "16px" }}>
                        パターン{selectedProject.patterns?.[0]?.pattern_number || 1}
                    </span>
                )}
            </div>
            <div style={{ marginBottom: "12px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                {selectedProject && (
                    <>
                        <button type="button" onClick={handleAddType} className="wlm-btn-add" style={{ backgroundColor: "#16a34a", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", cursor: "pointer" }}>
                            タイプ追加
                        </button>
                        <button type="button" onClick={handleSave} disabled={saving} style={{ backgroundColor: "#2563eb", color: "white", border: "none", padding: "6px 16px", borderRadius: "4px", fontWeight: "bold", cursor: saving ? "not-allowed" : "pointer" }}>
                            {saving ? "保存中…" : "保存"}
                        </button>
                    </>
                )}
                <button type="button" onClick={() => navigate(-1)} style={{ backgroundColor: "#0099ff", color: "white", border: "none", padding: "6px 16px", borderRadius: "4px", fontWeight: "bold", cursor: "pointer" }}>
                    戻る
                </button>
            </div>

            {msg && (
                <div style={{ marginBottom: "8px", color: msg.includes("失敗") ? "#dc2626" : "#16a34a", fontWeight: "bold" }}>
                    {msg}
                </div>
            )}

            {selectedProject && (
                <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", flexWrap: "wrap" }}>
                    {/* 左：10行目まで */}
                    <div style={{ flex: "0 1 auto" }}>
                        {renderTable(leftTypes)}
                    </div>
                    {/* 右：11行目から（11件以上あるときのみ表示） */}
                    {rightTypes.length > 0 && (
                        <div style={{ flex: "0 1 auto"}}>
                            {renderTable(rightTypes)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProjectPatternViewer;
